import { useCallback, useMemo } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import type { AppliedFilters } from '@/components/FilterBar/filters';
import {
    isOrderFilterKey,
    ORDER_FILTER_KEY_LIST,
    type OrderFilterKey,
} from '@/features/orders/orderFilters';
import { isOrderSortKey } from '@/features/orders/orders';
import { OrdersPage, type OrdersSearch } from '@/features/orders/OrdersPage';

/*
  The orders register. The search, the applied filters, the sort and the page all ride the URL, so
  a filtered view can be shared and a reload restores it exactly (ALS-56).

  Every filter is an optional string here and nothing more: the drawer owns what each one means
  and the API validates the few that are closed sets, so the route's job is to carry them
  faithfully and to refuse nothing. `.catch(undefined)` keeps a junk value in a hand-edited URL
  from throwing the route; the page falls back to the register's defaults instead.
*/
const param = z.string().optional().catch(undefined);

/*
  The filter keys, built from the same list the drawer is built from so the two cannot drift: a
  key added there rides the URL without being named again here, as Commission Model did. The cast is what keeps
  the shape precise; without it `Object.fromEntries` widens to an index signature and `page` loses
  its number.
*/
const filterShape = Object.fromEntries(ORDER_FILTER_KEY_LIST.map((key) => [key, param])) as Record<
    OrderFilterKey,
    typeof param
>;

const ordersSearch = z.object({
    q: param,
    page: z.coerce.number().int().min(1).optional().catch(undefined),
    sort: param,
    dir: param,
    ...filterShape,
});

type RawSearch = z.infer<typeof ordersSearch>;

/** The applied set as the URL carries it, with blanks dropped so they never draw a chip. */
function filtersFrom(raw: RawSearch): AppliedFilters<OrderFilterKey> {
    const out: AppliedFilters<OrderFilterKey> = {};
    for (const key of ORDER_FILTER_KEY_LIST) {
        const value = raw[key];
        if (typeof value === 'string' && value.trim() !== '') out[key] = value;
    }
    return out;
}

export const Route = createFileRoute('/_app/orders/')({
    validateSearch: (search) => ordersSearch.parse(search),
    component: OrdersRoute,
});

function OrdersRoute() {
    const raw = Route.useSearch();
    const navigate = useNavigate();

    const search = useMemo<OrdersSearch>(() => {
        const sort = raw.sort && isOrderSortKey(raw.sort) ? raw.sort : null;
        return {
            q: raw.q ?? '',
            filters: filtersFrom(raw),
            sort: sort ? { key: sort, direction: raw.dir === 'desc' ? 'desc' : 'asc' } : null,
            page: raw.page ?? 1,
        };
    }, [raw]);

    const onSearchChange = useCallback(
        (next: OrdersSearch) => {
            const filters: Partial<Record<OrderFilterKey, string>> = {};
            for (const [key, value] of Object.entries(next.filters)) {
                // A multi field's list would need encoding; no order filter is one, so a list
                // reads as absent rather than being stringified into something unparseable.
                if (isOrderFilterKey(key) && typeof value === 'string' && value.trim() !== '') {
                    filters[key] = value;
                }
            }
            void navigate({
                to: '/orders',
                search: {
                    ...filters,
                    q: next.q.trim() || undefined,
                    page: next.page > 1 ? next.page : undefined,
                    sort: next.sort?.key,
                    dir: next.sort?.direction,
                },
                replace: true,
                resetScroll: false,
            });
        },
        [navigate],
    );

    return <OrdersPage search={search} onSearchChange={onSearchChange} />;
}
